import { toggleSubjectStatusApi } from "@/api/services/admin/learning-content";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { TSubjectInfo } from "@/features/learning-content/types/learningContent";
import { useTable } from "@/hooks/useTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { button, divider, text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { Box, Switch as MuiSwitch, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type TSubjectsTableProps = {
  isLoading: boolean;
  isFetching?: boolean;
  data: TSubjectInfo[];
  learningContentId: string | number;
  onDragEnd: (draggedRow: MRT_Row<TSubjectInfo>, targetRow: MRT_Row<TSubjectInfo> | null) => void;
};

export const SubjectsTable: FC<TSubjectsTableProps> = ({ isLoading, isFetching, data, learningContentId, onDragEnd }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const handleToggleActive = async (id: number, currentIsActive: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(id));
    try {
      await toggleSubjectStatusApi(String(id), { isActive: !currentIsActive });
      toast.success(API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_SUBJECT_LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, String(learningContentId)] });
    } catch (error) {
      showError(error);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Calculate totals and pre-compute active week indices for O(1) lookup per row
  const { totals, activeWeekIndices } = useMemo(() => {
    let activeCount = 0;
    let vocabularyCount = 0;
    let questionCount = 0;
    const indices = new Map<number, number>();

    data.forEach((item) => {
      if (item.isActive) {
        activeCount++;
        vocabularyCount += item.vocabularyCount || 0;
        questionCount += item.questionCount || 0;
        indices.set(item.id, activeCount);
      }
    });

    return {
      totals: { activeCount, vocabularyCount, questionCount },
      activeWeekIndices: indices,
    };
  }, [data]);

  const columns = useMemo<MRT_ColumnDef<TSubjectInfo>[]>(
    () => [
      {
        header: "有効",
        accessorKey: "isActive",
        size: 84,
        muiTableHeadCellProps: { sx: { width: "84px" } },
        muiTableBodyCellProps: { sx: { width: "84px" } },
        Cell: ({ row }) => {
          const { id, isActive, questionCount } = row.original;
          const hasNoQuestions = (questionCount ?? 0) === 0;
          return <IOSSwitch checked={isActive} disabled={togglingIds.has(id) || hasNoQuestions} onChange={() => handleToggleActive(id, isActive)} />;
        },
        Footer: "",
        enableSorting: false,
      },
      {
        header: "週",
        id: "weekNumber",
        size: 46,
        muiTableHeadCellProps: { sx: { width: "46px", textAlign: "center", "& .Mui-TableHeadCell-Content": { justifyContent: "center" } } },
        muiTableBodyCellProps: { sx: { width: "46px", textAlign: "center" } },
        Cell: ({ row }) => {
          const weekNumber = activeWeekIndices.get(row.original.id);
          return <Box sx={{ textAlign: "center", width: "100%" }}>{weekNumber ?? "-"}</Box>;
        },
        Footer: "",
        enableSorting: false,
      },
      {
        header: "WEEK名",
        accessorKey: "subjectName",
        grow: true,
        muiTableHeadCellProps: { sx: { flex: 1 } },
        muiTableBodyCellProps: { sx: { flex: 1 } },
        muiTableFooterCellProps: { sx: { textAlign: "right" } },
        Cell: ({ row }) => {
          const subjectName = row.original.subjectName;
          if (!subjectName) return EMPTY_STR.TEXT;
          const detailUrl = getDynamicRoute(AppRoutes.learningContentSubjectDetail, {
            id: String(learningContentId),
            subjectId: String(row.original.id),
          });
          return <Link to={detailUrl}>{subjectName}</Link>;
        },
        Footer: () => (
          <Typography variant="body14" sx={{ fontWeight: 700 }}>
            有効なWEEKの合計：
          </Typography>
        ),
      },
      {
        header: "単語数",
        accessorKey: "vocabularyCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => {
          const count = row.original.vocabularyCount ?? 0;
          return count === 0 ? <Typography sx={{ color: "#E57373" }}>{count}</Typography> : count;
        },
        Footer: () => <Typography variant="body14">{totals.vocabularyCount}</Typography>,
      },
      {
        header: "問題数",
        accessorKey: "questionCount",
        size: 74,
        muiTableHeadCellProps: { sx: { width: "74px" } },
        muiTableBodyCellProps: { sx: { width: "74px" } },
        Cell: ({ row }) => {
          const count = row.original.questionCount ?? 0;
          return count === 0 ? <Typography sx={{ color: "#E57373" }}>{count}</Typography> : count;
        },
        Footer: () => <Typography variant="body14">{totals.questionCount}</Typography>,
      },
      {
        header: "最終更新日",
        accessorKey: "lastUpdatedDate",
        size: 106,
        muiTableHeadCellProps: { sx: { width: "106px" } },
        muiTableBodyCellProps: { sx: { width: "106px" } },
        Cell: ({ row }) => {
          return row.original.lastUpdatedDate || EMPTY_STR.TEXT;
        },
        Footer: "",
      },
    ],
    [togglingIds, learningContentId, activeWeekIndices, totals],
  );

  const { table } = useTable({
    columns,
    rows: data ?? [],
    enableSorting: false,
    enableRowActions: false,
    enableRowOrdering: true,
    enableTableFooter: true,
    displayColumnDefOptions: {
      "mrt-row-drag": {
        header: "",
        size: 43,
      },
    },
    muiRowDragHandleProps: ({ table: tableInstance }) => ({
      onDragEnd: () => {
        const { draggingRow, hoveredRow } = tableInstance.getState();
        if (draggingRow && hoveredRow) {
          onDragEnd(draggingRow, hoveredRow as MRT_Row<TSubjectInfo>);
        }
      },
    }),
  });

  return (
    <TableWrapper>
      <Table table={table} isLoading={isLoading} isFetching={isFetching} />
    </TableWrapper>
  );
};

const TableWrapper = styled(Box)({
  // Header cells: match Figma 14px bold
  "& .MuiTableCell-head": {
    fontSize: "14px !important",
  },
  // Footer row: no borders, text color matches primary
  "& .MuiTableFooter-root .MuiTableCell-root": {
    borderTop: `1px solid ${divider.middle}`,
    borderBottom: 0,
    color: text.primary,
    height: "53px",
    verticalAlign: "middle",
  },
});

const IOSSwitch = styled(MuiSwitch)(({ theme }) => ({
  width: 47,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    color: "#ABB4B6",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      marginLeft: 7,
      "& + .MuiSwitch-track": {
        backgroundColor: button.primary,
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: button.primary,
      border: "6px solid #fff",
    },
    "&.Mui-disabled": {
      color: "#CACDCD",
      cursor: "not-allowed",
      "& .MuiSwitch-thumb": {
        color: "#CACDCD",
      },
      "& + .MuiSwitch-track": {
        backgroundColor: "#E6E7E8",
        border: 0,
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    border: "1px solid #ABB4B6",
    backgroundColor: theme.palette.mode === "light" ? "#FFFFFF" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));
